import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _, { noop } from 'underscore';
import classNames from 'classnames';
import { withCustomValidation } from '@bingads-webui-react/validation';
import {
  useDefaultStyles,
  withDefaultStyles,
} from '@bingads-webui-react/with-default-styles';
import { ImpressionTrackingUrlOptions as ImpressionTrackingView } from '@bingads-webui-campaign/audience-campaign-shared';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { adEditorOverrideStyle } from '@bingads-webui-campaign-react/ad-editor-styles';

const getTopGridPanelCustomStyles = (__, palette, iconFontFamily) =>
  Object.assign({}, adEditorOverrideStyle({ palette, iconFontFamily }));

const extractImpressionTrackingSetting = (impressionTrackingUrls) => {
  if (_.isArray(impressionTrackingUrls)) {
    return impressionTrackingUrls;
  }
  return ['', ''];
};

export const formatImpressionTrackingSetting = (
  impressionTrackingUrls,
  i18n
) => {
  const [firstUrl, secondUrl] = extractImpressionTrackingSetting(impressionTrackingUrls);

  const getImpressionTrackingComponent = url => (
    <div>
      {i18n.getString(_TL_('Impression tracking URL: ')) +
        (!url || url === '' ? i18n.getString(_TL_('None')) : url)}
    </div>
  );

  return (
    <div white-space="pre">
      {getImpressionTrackingComponent(firstUrl)}
      {getImpressionTrackingComponent(secondUrl)}
    </div>
  );
};

const ImpressionTrackingSetting = ({
  i18n,
  impressionTrackingUrls,
  updateValidation,
}) => {
  const classes = useDefaultStyles();

  const [
    impressionTrackingSettingRef,
    setImpressionTrackingSettingRef,
  ] = useState();

  useEffect(() => {
    updateValidation([], { ImpressionTrackingUrls: impressionTrackingUrls });
  }, [impressionTrackingUrls, updateValidation]);

  const legacyI18n = useMemo(
    () => window.globalLegacyI18n.get('$/i18n!legacy/global'),
    []
  );
  const i18nMerged = useMemo(() => mergeI18n(legacyI18n, i18n), [
    legacyI18n,
    i18n,
  ]);

  const impressionTrackingViewReact = useMemo(
    () => {
      const impressionTrackingView = new ImpressionTrackingView({
        i18n: i18nMerged,
        initialImpressionTrackingUrls: impressionTrackingUrls,
        isIASSectionVisible: false,
        isVerticalUrlInput: true,
      });

      impressionTrackingView.render();

      return impressionTrackingView;
    },
    [impressionTrackingUrls, i18nMerged]
  );

  useEffect(
    () => {
      if (impressionTrackingSettingRef) {
        impressionTrackingSettingRef.innerHTML = '';
        impressionTrackingSettingRef.append(impressionTrackingViewReact.el);

        impressionTrackingViewReact.on('InputChange', (value) => {
          updateValidation([], {
            ImpressionTrackingUrls: value,
          });
        });
      }

      return noop;
    },
    [
      impressionTrackingSettingRef,
      impressionTrackingViewReact,
      updateValidation,
    ]
  );

  return (
    <div
      className={classNames([
        'impression-tracking-setting-picker',
        classes.pickerContainer,
      ])}
    >
      <div>
        {i18n.getString(_TL_('Set up the third-party impression tracking information for your ads.'))}
      </div>
      <div
        className={classNames(['impression-tracking-setting-view'])}
        ref={newRef => setImpressionTrackingSettingRef(newRef)}
      />
    </div>
  );
};

ImpressionTrackingSetting.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  impressionTrackingUrls: PropTypes.shape(PropTypes.string).isRequired,
  updateValidation: PropTypes.func.isRequired,
};

const validatedImpressionTrackingSetting = withDefaultStyles(
  withCustomValidation(ImpressionTrackingSetting),
  getTopGridPanelCustomStyles
);

export { validatedImpressionTrackingSetting as ImpressionTrackingSetting };
